import type { ReviewSummaryRow, PlayProposalReview } from '@/types/gtm-agents';

/**
 * A workstream card's few label and state rows; the requirements and choices
 * behind them stay in the card's expandable evidence. The rows are derived
 * from the review the card already carries, so they never change what is
 * confirmed.
 */
export function questionReviewSummary(
  requirements: number,
  choices: number
): ReviewSummaryRow[] {
  return [
    { label: 'Workstream', state: 'Awaiting confirmation' },
    { label: 'Requirements', state: `${requirements} to review` },
    ...(choices ? [{ label: 'Choices', state: `${choices} resolved` }] : []),
  ];
}

/**
 * A proposal's few label and state rows, from the requirements and blockers
 * it already carries; the evidence stays expandable. They never change
 * whether it can be accepted.
 */
export function proposalReviewSummary(
  proposal: PlayProposalReview,
  blocked: boolean
): ReviewSummaryRow[] {
  const requirements = proposal.requirements ?? [];
  const rows = [
    {
      label: 'Candidate',
      state: blocked ? 'Needs changes' : 'Ready for review',
    },
  ];
  if (!requirements.length) return rows;
  const confirmed = requirements.filter(
    (r) => r.state === 'user_confirmed'
  ).length;
  const supported = requirements.filter(
    (r) => r.coverage === 'supported'
  ).length;
  const counts = (first: number, firstWord: string, secondWord: string) =>
    [
      first ? `${first} ${firstWord}` : '',
      requirements.length > first
        ? `${requirements.length - first} ${secondWord}`
        : '',
    ]
      .filter(Boolean)
      .join(', ');
  rows.push(
    {
      label: 'Requirements',
      state: counts(confirmed, 'confirmed', 'inferred'),
    },
    { label: 'Coverage', state: counts(supported, 'supported', 'unverified') }
  );
  return rows;
}
